import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';

const KVKK: React.FC = () => {
    useDocumentTitle('KVKK Aydınlatma Metni | CO-HR');
    const history = useHistory();

    return (
        <div className="min-h-screen bg-white font-sans">
            <header className="sticky top-0 bg-white/90 backdrop-blur-xl border-b border-gray-100 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <button onClick={() => history.push('/')} className="text-xl font-extrabold bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent focus:outline-none">
                        CO-HR
                    </button>
                    <button onClick={() => history.goBack()} className="text-sm text-gray-500 hover:text-gray-900 transition focus:outline-none">
                        &larr; Geri Dön
                    </button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">KVKK Aydınlatma Metni</h1>
                <p className="text-gray-400 text-sm mb-2">6698 Sayılı Kişisel Verilerin Korunması Kanunu Kapsamında</p>
                <p className="text-gray-400 text-sm mb-10">Son güncelleme: 1 Nisan 2026</p>

                <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Veri Sorumlusu</h2>
                        <p>
                            6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında, kişisel verileriniz veri sorumlusu
                            sıfatıyla CO-HR tarafından aşağıda açıklanan kapsamda işlenmektedir.
                        </p>
                        <p className="mt-2">
                            <strong>Veri Sorumlusu:</strong> CO-HR Yazılım Teknolojileri<br />
                            <strong>Web:</strong>{' '}
                            <a href="https://co-hr.com.tr" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                co-hr.com.tr
                            </a><br />
                            <strong>E-posta:</strong> kvkk@co-hr.com.tr
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. İşlenen Kişisel Veriler</h2>
                        <p>Platform kapsamında aşağıdaki kişisel veri kategorileri işlenmektedir:</p>

                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-200">
                                        <th className="text-left py-3 pr-4 font-semibold text-gray-900">Veri Kategorisi</th>
                                        <th className="text-left py-3 font-semibold text-gray-900">Açıklama</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    <tr>
                                        <td className="py-3 pr-4 font-medium">Kimlik Bilgileri</td>
                                        <td className="py-3">Ad, soyad, sicil numarası, T.C. kimlik numarası</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 pr-4 font-medium">İletişim Bilgileri</td>
                                        <td className="py-3">E-posta adresi, telefon numarası</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 pr-4 font-medium">Mesleki Bilgiler</td>
                                        <td className="py-3">Departman, ünvan, yönetici bilgisi, işe başlama tarihi</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 pr-4 font-medium">Performans Verileri</td>
                                        <td className="py-3">Hedefler, OKR'lar, değerlendirme puanları, yetkinlik değerlendirmeleri, geri bildirimler</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 pr-4 font-medium">İşlem Güvenliği</td>
                                        <td className="py-3">IP adresi, oturum bilgileri, erişim logları, tarayıcı bilgileri</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Kişisel Verilerin İşlenme Amaçları</h2>
                        <p>Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>İnsan kaynakları süreçlerinin yürütülmesi</li>
                            <li>Performans değerlendirme ve hedef yönetimi hizmetlerinin sunulması</li>
                            <li>360° geri bildirim süreçlerinin planlanması ve yürütülmesi</li>
                            <li>Yetkinlik değerlendirmelerinin yapılması</li>
                            <li>Raporlama ve istatistiksel analizlerin gerçekleştirilmesi</li>
                            <li>Kullanıcı yetkilendirme ve erişim kontrolünün sağlanması</li>
                            <li>Bilgi güvenliği süreçlerinin yürütülmesi</li>
                            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Kişisel Verilerin İşlenme Hukuki Sebepleri</h2>
                        <p>Kişisel verileriniz, KVKK'nın 5. ve 6. maddelerinde belirtilen aşağıdaki hukuki sebeplere dayanılarak işlenmektedir:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li><strong>Sözleşmenin ifası:</strong> Hizmet sözleşmesinin kurulması ve yürütülmesi için gerekli olması</li>
                            <li><strong>Meşru menfaat:</strong> Veri sorumlusunun meşru menfaatleri için veri işlemenin zorunlu olması</li>
                            <li><strong>Hukuki yükümlülük:</strong> Kanunlarda açıkça öngörülmüş olması</li>
                            <li><strong>Açık rıza:</strong> Yukarıdaki hukuki sebeplere dayanılarak işlenemeyen veriler için ilgili kişinin açık rızası</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Kişisel Verilerin Aktarılması</h2>
                        <p>
                            Kişisel verileriniz, yukarıda belirtilen amaçlar doğrultusunda ve KVKK'nın 8. ve 9. maddelerine uygun olarak
                            aşağıdaki alıcı gruplarına aktarılabilir:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li><strong>İşveren kuruluş:</strong> Performans değerlendirme süreçleri kapsamında yetkili yöneticilere</li>
                            <li><strong>Hizmet sağlayıcılar:</strong> Bulut altyapı, barındırma ve teknik destek hizmeti sağlayan iş ortaklarına</li>
                            <li><strong>Yetkili kamu kurum ve kuruluşları:</strong> Yasal zorunluluk halinde ilgili mercilere</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Veri Saklama Süreleri</h2>
                        <p>
                            Kişisel verileriniz, işlenme amaçlarının gerektirdiği süre boyunca saklanır. İlgili mevzuatta öngörülen
                            saklama süreleri saklı kalmak kaydıyla, işlenme amacı ortadan kalkan veriler silinir, yok edilir veya
                            anonim hale getirilir.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Veri Güvenliği Tedbirleri</h2>
                        <p>CO-HR, kişisel verilerinizin güvenliğini sağlamak amacıyla aşağıdaki tedbirleri almaktadır:</p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li>SSL/TLS şifreleme ile veri iletim güvenliği</li>
                            <li>Veritabanı şifreleme ve erişim kontrolleri</li>
                            <li>Düzenli güvenlik testleri ve denetimleri</li>
                            <li>Çalışanlar için veri güvenliği eğitimleri</li>
                            <li>Erişim loglarının izlenmesi ve yetkilendirme mekanizmaları</li>
                            <li>Yedekleme ve felaket kurtarma prosedürleri</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. İlgili Kişinin Hakları (KVKK Madde 11)</h2>
                        <p>KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:</p>
                        <div className="mt-3 bg-gray-50 rounded-xl p-5 space-y-2">
                            <p><strong>a)</strong> Kişisel verilerinizin işlenip işlenmediğini öğrenme</p>
                            <p><strong>b)</strong> Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme</p>
                            <p><strong>c)</strong> Kişisel verilerinizin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</p>
                            <p><strong>d)</strong> Yurt içinde veya yurt dışında kişisel verilerinizin aktarıldığı üçüncü kişileri bilme</p>
                            <p><strong>e)</strong> Kişisel verilerinizin eksik veya yanlış işlenmiş olması halinde bunların düzeltilmesini isteme</p>
                            <p><strong>f)</strong> KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerinizin silinmesini veya yok edilmesini isteme</p>
                            <p><strong>g)</strong> (e) ve (f) bentleri uyarınca yapılan işlemlerin, kişisel verilerinizin aktarıldığı üçüncü kişilere bildirilmesini isteme</p>
                            <p><strong>h)</strong> İşlenen verilerinizin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</p>
                            <p><strong>ı)</strong> Kişisel verilerinizin kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Başvuru Yöntemi</h2>
                        <p>
                            Yukarıda belirtilen haklarınızı kullanmak için aşağıdaki yöntemlerle başvurabilirsiniz:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                            <li><strong>E-posta:</strong> kvkk@co-hr.com.tr adresine kimlik teyit bilgileriniz ile birlikte</li>
                            <li><strong>Yazılı başvuru:</strong> Noter aracılığıyla veya iadeli taahhütlü posta ile şirket adresimize</li>
                        </ul>
                        <p className="mt-2">
                            Başvurularınız, talebin niteliğine göre en kısa sürede ve en geç <strong>30 gün</strong> içinde
                            ücretsiz olarak sonuçlandırılacaktır. İşlemin ayrıca bir maliyet gerektirmesi halinde,
                            Kişisel Verileri Koruma Kurulu tarafından belirlenen tarifedeki ücret alınabilir.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. İletişim Bilgileri</h2>
                        <p>
                            <strong>Veri Sorumlusu:</strong> CO-HR Yazılım Teknolojileri<br />
                            <strong>E-posta:</strong> kvkk@co-hr.com.tr<br />
                            <strong>Web:</strong>{' '}
                            <a href="https://co-hr.com.tr" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                co-hr.com.tr
                            </a>
                        </p>
                    </section>
                </div>
            </main>

            <footer className="border-t border-gray-100 bg-gray-50 py-6">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-gray-400 text-sm">
                    Bu bir{' '}
                    <a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-600 hover:text-indigo-500 transition">
                        CO-HR
                    </a>
                    {' '}ürünüdür.
                </div>
            </footer>
        </div>
    );
};

export default KVKK;
